import { Link } from 'react-router-dom';
import { Building2, Mail, Phone, MapPin, Facebook, Instagram } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-primary text-primary-foreground" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_gk-properties/artifacts/bexs4tg2_file_00000000f03c7209b29acd69cf9894f8.png" 
                alt="GK Group of Properties Logo" 
                className="h-28 md:h-32 w-auto object-contain brightness-0 invert"
              />
            </div>
            <p className="text-sm leading-relaxed opacity-90 mb-4">
              GK Group has been recognized as a distinguished real estate developer in Odisha.
            </p>
          </div>

          {/* Our Projects */}
          <div>
            <h3 className="font-heading text-lg mb-6 tracking-wide">Our Projects</h3>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/properties#ongoing" 
                  className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 block"
                  data-testid="footer-ongoing"
                >
                  Ongoing Projects
                </Link>
              </li>
              <li>
                <Link 
                  to="/properties#upcoming" 
                  className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 block"
                  data-testid="footer-upcoming"
                >
                  Upcoming Projects
                </Link>
              </li>
              <li>
                <Link 
                  to="/properties#completed" 
                  className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 block"
                  data-testid="footer-completed"
                >
                  Completed Projects
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading text-lg mb-6 tracking-wide">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/" 
                  className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 block"
                  data-testid="footer-home"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link 
                  to="/about" 
                  className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 block"
                  data-testid="footer-about"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link 
                  to="/properties" 
                  className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 block"
                  data-testid="footer-properties"
                >
                  Our Projects
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 block"
                  data-testid="footer-contact"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link 
                  to="/enquiry" 
                  className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 block"
                >
                  Enquiry Form
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy-policy" 
                  className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 block"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms-conditions" 
                  className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 block"
                >
                  Terms & Conditions
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading text-lg mb-6 tracking-wide">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                <div className="text-sm opacity-90">
                  <a href="tel:+917008078580" className="hover:text-secondary transition-colors block">+91 7008078580</a>
                  <a href="tel:+919922408614" className="hover:text-secondary transition-colors block">+91 9922408614</a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                <a href="mailto: info@confinitygkgroup.com" className="text-sm opacity-90 hover:text-secondary transition-colors">
                   info@confinitygkgroup.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-sm opacity-90 leading-relaxed">
                  Flat No. 403, Siba Plaza Apartment, Gajapati Nagar, Bhubaneswar, Odisha - 751005
                </span>
              </li>
            </ul>
            
            <div className="flex items-center gap-4 mt-6">
              <a 
                href="https://www.facebook.com/share/1HNWZ7FKJi/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border-2 border-primary-foreground/30 hover:border-secondary hover:bg-secondary/10 flex items-center justify-center transition-all duration-300"
                aria-label="Facebook"
                data-testid="facebook-link"
              >
                <Facebook className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a 
                href="https://www.instagram.com/gkgroupofproperties/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border-2 border-primary-foreground/30 hover:border-secondary hover:bg-secondary/10 flex items-center justify-center transition-all duration-300"
                aria-label="Instagram"
                data-testid="instagram-link"
              >
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-primary-foreground/20">
          <p className="text-sm opacity-80 text-center">
            © Copyright 2026 GK Group | Brand by GK Group of Properties | All rights reserved 
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
